/* eslint-disable @typescript-eslint/no-this-alias */
declare let mxClient: any;
declare let mxConstants: any;
declare let mxGraph: any;
declare let mxEventSource: any;
declare let mxRectangleShape: any;
declare let mxCodec: any;
declare let mxEventObject: any;
declare let mxCellMarker: any;
declare let mxUtils: any;
declare let mxResources: any;
declare let mxGraphView: any;
declare let mxRectangle: any;
declare let mxGraphHandler: any;
declare let mxImageShape: any;
declare let mxConnectionHandler: any;
declare let mxEvent: any;
declare let mxMouseEvent: any;
declare let mxPolyline: any;
declare let mxEdgeHandler: any;
declare let mxGuide: any;
declare let mxPoint: any;
declare let mxPopupMenu: any;
declare let mxPopupMenuHandler: any;
declare let mxEdgeStyle: any;
declare let mxStyleRegistry: any;

import {Injectable} from '@angular/core';
import {Q9Graph} from '../graph/graph';
import {SidebarService} from '../../../editor-interface/sidebar/sidebar.service';

@Injectable()
export class EditorService {
  q9Editor;

  constructor(q9Graph: Q9Graph, sidebarSvc: SidebarService) {
    const IMAGE_PATH = 'images';

    function Editor() {
      mxEventSource.call(this);

      this.init();
      this.graph = this.createGraph();

      // Updates modified state if graph changes
      this.graphChangeListener = function () {
        this.setModified(true);
      };

      this.graph.getModel().addListener(
        mxEvent.CHANGE,
        mxUtils.bind(this, function () {
          // eslint-disable-next-line prefer-spread
          this.graphChangeListener.apply(this, arguments);
        })
      );

      this.graph.addListener(mxEvent.EDITING_STARTED, () => {
        sidebarSvc.refresh();
      });

      this.graph.addListener(mxEvent.EDITING_STOPPED, () => {
        sidebarSvc.refresh();
      });

      // Sets persistent graph state defaults
      this.graph.resetViewOnRootChange = false;
    }

    // Editor inherits from mxEventSource
    mxUtils.extend(Editor, mxEventSource);

    /**
     * Specifies the image URL to be used for the grid.
     */
    Editor.prototype.gridImage = mxClient.IS_SVG
      ? 'data:image/gif;base64,R0lGODlhCgAKAJEAAAAAAP///8zMzP///yH5BAEAAAMALAAAAAAKAAoAAAIJ1I6py+0Po2wFADs='
      : IMAGE_PATH + '/grid.gif';

    /**
     * Scrollbars are enabled on non-touch devices (not including Firefox because touch events
     * cannot be detected in Firefox, see above).
     */
    Editor.prototype.defaultScrollbars = !mxClient.IS_IOS;

    /**
     * Specifies if the page should be visible for new files. Default is true.
     */
    Editor.prototype.defaultPageVisible = false;

    /**
     * Specifies the image URL to be used for the transparent background.
     */
    Editor.prototype.transparentImage = mxClient.IS_SVG
      ? // eslint-disable-next-line max-len
        'data:image/gif;base64,R0lGODlhMAAwAIAAAP///wAAACH5BAEAAAAALAAAAAAwADAAAAIxhI+py+0Po5y02ouz3rz7D4biSJbmiabqyrbuC8fyTNf2jef6zvf+DwwKh8Si8egpAAA7'
      : IMAGE_PATH + '/transparent.gif';

    /**
     * Specifies if the canvas should be extended in all directions. Default is true.
     */
    Editor.prototype.extendCanvas = true;

    /**
     * Specifies if the editor is enabled. Default is true.
     */
    Editor.prototype.enabled = false;

    /**
     * Contains the current modified state of the diagram-render. This is false for
     * new diagrams and after the diagram-render was saved.
     */
    Editor.prototype.modified = false;

    /**
     * Specifies the top spacing for the initial page view. Default is 0.
     */
    Editor.prototype.initialTopSpacing = 0;

    /**
     * Specifies the app name. Default is document.title.
     */
    Editor.prototype.appName = document.title;

    /**
     * Sets the XML node for the current diagram-render.
     */
    Editor.prototype.createGraph = function () {
      return new q9Graph.q9Graph();
    };

    /**
     * Sets the XML node for the current diagram-render.
     */
    Editor.prototype.resetGraph = function () {
      this.graph.gridEnabled = true;
      this.graph.graphHandler.guidesEnabled = true;
      this.graph.setTooltips(true);
      this.graph.setConnectable(true);
      this.graph.foldingEnabled = false;
      this.graph.scrollbars = this.defaultScrollbars;
      this.graph.pageVisible = this.defaultPageVisible;
      this.graph.pageBreaksVisible = this.graph.pageVisible;
      this.graph.preferPageSize = this.graph.pageBreaksVisible;
      this.graph.background = null;
      this.graph.pageScale = mxGraph.prototype.pageScale;
      this.graph.pageFormat = mxGraph.prototype.pageFormat;
      this.updateGraphComponents();
      this.graph.view.setScale(1);
    };

    /**
     * Sets the XML node for the current diagram-render.
     */
    Editor.prototype.setGraphXml = function (node) {
      if (node != null) {
        // const model = this.graph.model;
        // const activityLayerCell = model.getCell(2);
        // const children = model.getChildren(activityLayerCell);
        // if (Array.isArray(children)) {
        //   children.forEach((cell) => compSvc.destroyComponent(cell));
        // }

        const dec = new mxCodec(node.ownerDocument);

        if (node.nodeName == 'root') {
          // Workaround for invalid XML output in Firefox 20 due to bug in mxUtils.getXml
          const wrapper = dec.document.createElement('mxGraphModel');

          wrapper.appendChild(node);

          dec.decode(wrapper, this.graph.getModel());
          this.updateGraphComponents();
          //this.fireEvent(new mxEventObject('resetGraphView'));
        } else {
          throw {
            message: mxResources.get('cannotOpenFile'),
            node: node,
            toString: function () {
              return this.message;
            }
          };
        }
      } else {
        this.resetGraph();
        this.graph.model.clear();
        this.fireEvent(new mxEventObject('resetGraphView'));
      }
    };

    /**
     * Keeps the graph container in sync with the persistent graph state
     */
    Editor.prototype.updateGraphComponents = function () {
      const graph = this.graph;

      if (graph.container != null) {
        const bg =
          graph.background == null || graph.background == 'none'
            ? 'var(--q9-surface-100)'
            : graph.background;

        if (graph.view.backgroundPageShape != null) {
          graph.view.backgroundPageShape.fill = bg;
          graph.view.backgroundPageShape.redraw();
        }

        // Transparent.gif is a workaround for focus repaint problems in IE and clipping issues in webkit
        const noBackground = 'url(' + this.transparentImage + ')';
        const bgImg =
          !graph.pageVisible && graph.isGridEnabled()
            ? 'url(' + this.gridImage + ')'
            : noBackground;

        // Needed to align background position for grid
        if (graph.isGridEnabled()) {
          graph.view.validateBackground();
        }

        if (graph.view.canvas.ownerSVGElement != null) {
          graph.view.canvas.ownerSVGElement.style.backgroundImage = bgImg;
        } else {
          graph.view.canvas.style.backgroundImage = bgImg;
        }

        graph.container.style.backgroundImage = noBackground;

        if (graph.view.backgroundPageShape != null) {
          graph.view.backgroundPageShape.node.style.backgroundImage = this.graph.isGridEnabled()
            ? 'url(' + this.gridImage + ')'
            : 'none';
        }

        graph.container.style.backgroundColor = bg;

        if (graph.pageVisible) {
          graph.container.style.backgroundColor = '#ebebeb';
          graph.container.style.borderStyle = 'solid';
          graph.container.style.borderColor = '#e5e5e5';
          graph.container.style.borderTopWidth = '1px';
          graph.container.style.borderLeftWidth = '1px';
          graph.container.style.borderRightWidth = '0px';
          graph.container.style.borderBottomWidth = '0px';
        } else {
          graph.container.style.border = '';
        }

        graph.container.style.overflow = graph.scrollbars ? 'auto' : 'hidden';

        this.fireEvent(new mxEventObject('updateGraphComponents'));
      }
    };

    /**
     * Sets the modified flag.
     */
    Editor.prototype.setModified = function (value) {
      this.modified = value;
    };

    /**
     * Initializes the environment.
     */
    Editor.prototype.init = function () {
      // Adds required resources (disables loading of fallback properties, this can only
      // be used if we know that all keys are defined in the language specific file)
      mxResources.loadDefaultBundle = false;

      // Makes the connection hotspot smaller
      mxConstants.DEFAULT_HOTSPOT = 0.3;

      const mxConnectionHandlerCreateMarker = mxConnectionHandler.prototype.createMarker;

      mxConnectionHandler.prototype.createMarker = function () {
        const marker = mxConnectionHandlerCreateMarker.apply(this, arguments);

        // Overrides to ignore hotspot only for target terminal
        marker.intersects = mxUtils.bind(this, function (state, evt) {
          if (this.isConnecting()) {
            return true;
          }

          return mxCellMarker.prototype.intersects.apply(marker, arguments);
        });

        return marker;
      };

      mxGraphView.prototype.getFixedTerminalPoint = function (edge, terminal, source, constraint) {
        let pt = null;

        if (constraint != null) {
          pt = this.graph.getConnectionPoint(terminal, constraint, false);
        }

        if (pt == null && terminal == null) {
          const s = this.scale;
          const tr = this.translate;
          const orig = edge.origin;
          const geo = this.graph.getCellGeometry(edge.cell);

          pt = geo.getTerminalPoint(source);

          if (pt != null) {
            pt = new mxPoint(s * (tr.x + pt.x + orig.x), s * (tr.y + pt.y + orig.y));
          }
        }

        return pt;
      };

      mxGraphView.prototype.transformControlPoint = function (state, pt, ignoreScale) {
        if (state != null && pt != null) {
          const orig = state.origin;
          const scale = ignoreScale ? 1 : this.scale;

          return new mxPoint(
            scale * (pt.x + this.translate.x + orig.x),
            scale * (pt.y + this.translate.y + orig.y)
          );
        }

        return null;
      };

      // Changes border color of background page shape
      mxGraphView.prototype.createBackgroundPageShape = function (bounds) {
        const bg =
          this.graph.background == null || this.graph.background == 'none'
            ? '#ffffff'
            : this.graph.background;

        return new mxRectangleShape(bounds, bg, '#cacaca');
      };

      // Fits the number of background pages to the graph
      mxGraphView.prototype.getBackgroundPageBounds = function () {
        const gb = this.getGraphBounds();

        // Computes unscaled, untranslated graph bounds
        const x = gb.width > 0 ? gb.x / this.scale - this.translate.x : 0;
        const y = gb.height > 0 ? gb.y / this.scale - this.translate.y : 0;
        const w = gb.width / this.scale;
        const h = gb.height / this.scale;

        const fmt = this.graph.pageFormat;
        const ps = this.graph.pageScale;

        const pw = fmt.width * ps;
        const ph = fmt.height * ps;

        const x0 = Math.floor(Math.min(0, x) / pw);
        const y0 = Math.floor(Math.min(0, y) / ph);
        const xe = Math.ceil(Math.max(1, x + w) / pw);
        const ye = Math.ceil(Math.max(1, y + h) / ph);

        const rows = xe - x0;
        const cols = ye - y0;

        const bounds = new mxRectangle(
          this.scale * (this.translate.x + x0 * pw),
          this.scale * (this.translate.y + y0 * ph),
          this.scale * rows * pw,
          this.scale * cols * ph
        );

        return bounds;
      };

      // Add panning for background page in VML
      const graphPanGraph = mxGraph.prototype.panGraph;

      mxGraph.prototype.panGraph = function (dx, dy) {
        graphPanGraph.apply(this, arguments);

        if (
          this.dialect != mxConstants.DIALECT_SVG &&
          this.view.backgroundPageShape != null &&
          (!this.useScrollbarsForPanning || !mxUtils.hasScrollbars(this.container))
        ) {
          this.view.backgroundPageShape.node.style.marginLeft = dx + 'px';
          this.view.backgroundPageShape.node.style.marginTop = dy + 'px';
        }
      };

      const editor = this;

      // Uses HTML for background pages (to support grid background image)
      mxGraphView.prototype.validateBackground = function () {
        const bg = this.graph.getBackgroundImage();

        if (bg != null) {
          if (this.backgroundImage == null || this.backgroundImage.image != bg.src) {
            if (this.backgroundImage != null) {
              this.backgroundImage.destroy();
            }

            const bounds = new mxRectangle(0, 0, 1, 1);

            this.backgroundImage = new mxImageShape(bounds, bg.src);
            this.backgroundImage.dialect = this.graph.dialect;
            this.backgroundImage.init(this.backgroundPane);
            this.backgroundImage.redraw();
          }

          this.redrawBackgroundImage(this.backgroundImage, bg);
        } else if (this.backgroundImage != null) {
          this.backgroundImage.destroy();
          this.backgroundImage = null;
        }

        if (this.graph.pageVisible && this.graph.container != null) {
          const bounds = this.getBackgroundPageBounds();

          if (this.backgroundPageShape == null) {
            this.backgroundPageShape = this.createBackgroundPageShape(bounds);
            this.backgroundPageShape.scale = 1;

            // Shadow filter causes problems in outline window in quirks mode. IE8 standards
            // also has known rendering issues inside mxWindow but not using shadow is worse.
            this.backgroundPageShape.isShadow = !mxClient.IS_QUIRKS;
            this.backgroundPageShape.dialect = mxConstants.DIALECT_STRICTHTML;
            this.backgroundPageShape.init(this.graph.container);

            // Required for the browser to render the background page in correct order
            this.graph.container.firstChild.style.position = 'absolute';
            this.graph.container.insertBefore(
              this.backgroundPageShape.node,
              this.graph.container.firstChild
            );
            this.backgroundPageShape.redraw();

            this.backgroundPageShape.node.className = 'geBackgroundPage';

            // Adds listener for double click handling on background
            mxEvent.addListener(
              this.backgroundPageShape.node,
              'dblclick',
              mxUtils.bind(this, function (evt) {
                this.graph.dblClick(evt);
              })
            );

            // Adds basic listeners for graph event dispatching outside of the
            // container and finishing the handling of a single gesture
            mxEvent.addGestureListeners(
              this.backgroundPageShape.node,
              mxUtils.bind(this, function (evt) {
                this.graph.fireMouseEvent(mxEvent.MOUSE_DOWN, new mxMouseEvent(evt));
              }),
              mxUtils.bind(this, function (evt) {
                // Hides the tooltip if mouse is outside container
                if (
                  this.graph.tooltipHandler != null &&
                  this.graph.tooltipHandler.isHideOnHover()
                ) {
                  this.graph.tooltipHandler.hide();
                }

                if (this.graph.isMouseDown && !mxEvent.isConsumed(evt)) {
                  this.graph.fireMouseEvent(mxEvent.MOUSE_MOVE, new mxMouseEvent(evt));
                }
              }),
              mxUtils.bind(this, function (evt) {
                this.graph.fireMouseEvent(mxEvent.MOUSE_UP, new mxMouseEvent(evt));
              })
            );
          } else {
            this.backgroundPageShape.scale = 1;
            this.backgroundPageShape.bounds = bounds;
            this.backgroundPageShape.redraw();

            const bds = this.getBackgroundPageBounds();
            const gs = this.graph.gridSize * this.graph.view.scale;
            const tx =
              -1 +
              (gs -
                mxUtils.mod(bds.x / this.graph.view.scale - this.translate.x, gs) *
                  this.graph.view.scale);
            const ty =
              -1 +
              (gs -
                mxUtils.mod(bds.y / this.graph.view.scale - this.translate.y, gs) *
                  this.graph.view.scale);

            this.backgroundPageShape.node.style.backgroundPosition =
              Math.round(tx) + 'px ' + Math.round(ty) + 'px';
          }

          this.backgroundPageShape.node.style.backgroundImage = this.graph.isGridEnabled()
            ? 'url(' + editor.gridImage + ')'
            : 'none';
        } else if (this.backgroundPageShape != null) {
          this.backgroundPageShape.destroy();
          this.backgroundPageShape = null;
        }
      };

      /* Fix rectangle rounding so it is consistent (not based on size)
       * Rounded corners based on value of constant: RECTANGLE_ROUNDING_FACTOR
       * Border radius is normally (but oddly) figured by multiplying by shape dimensions.
       * Instead of using the current height and width, this pseudo-constant is used to figure the appropriate radius.
       */

      this.BORDER_RADIUS_BASIS = 130;

      const that = this;

      mxRectangleShape.prototype.paintBackground = function (c, x, y, w, h) {
        let events = true;

        if (this.style != null) {
          events = mxUtils.getValue(this.style, mxConstants.STYLE_POINTER_EVENTS, '1') == '1';
        }

        if (
          events ||
          (this.fill != null && this.fill != mxConstants.NONE) ||
          (this.stroke != null && this.stroke != mxConstants.NONE)
        ) {
          if (!events && (this.fill == null || this.fill == mxConstants.NONE)) {
            c.pointerEvents = false;
          }

          if (this.isRounded) {
            const f =
              mxUtils.getValue(
                this.style,
                mxConstants.STYLE_ARCSIZE,
                mxConstants.RECTANGLE_ROUNDING_FACTOR * 100
              ) / 100;

            const r = Math.min(f * that.BORDER_RADIUS_BASIS, f * that.BORDER_RADIUS_BASIS);

            c.roundrect(x, y, w, h, r, r);
          } else {
            c.rect(x, y, w, h);
          }

          c.fillAndStroke();
        }
      };

      // Draws page breaks only within the page
      mxGraph.prototype.updatePageBreaks = function (visible, width, height) {
        const scale = this.view.scale;
        const tr = this.view.translate;
        const fmt = this.pageFormat;
        const ps = scale * this.pageScale;

        const bounds2 = this.view.getBackgroundPageBounds();

        width = bounds2.width;
        height = bounds2.height;
        const bounds = new mxRectangle(scale * tr.x, scale * tr.y, fmt.width * ps, fmt.height * ps);

        // Does not show page breaks if the scale is too small
        visible = visible && Math.min(bounds.width, bounds.height) > this.minPageBreakDist;

        const horizontalCount = visible ? Math.ceil(width / bounds.width) - 1 : 0;
        const verticalCount = visible ? Math.ceil(height / bounds.height) - 1 : 0;
        const right = bounds2.x + width;
        const bottom = bounds2.y + height;

        if (this.horizontalPageBreaks == null && horizontalCount > 0) {
          this.horizontalPageBreaks = [];
        }

        if (this.horizontalPageBreaks != null) {
          for (let i = 0; i <= horizontalCount; i++) {
            const pts = [
              new mxPoint(bounds2.x + (i + 1) * bounds.width, bounds2.y),
              new mxPoint(bounds2.x + (i + 1) * bounds.width, bottom)
            ];

            if (this.horizontalPageBreaks[i] != null) {
              this.horizontalPageBreaks[i].points = pts;
              this.horizontalPageBreaks[i].redraw();
            } else {
              const pageBreak = new mxPolyline(pts, this.pageBreakColor);

              pageBreak.dialect = this.dialect;
              pageBreak.isDashed = this.pageBreakDashed;
              pageBreak.pointerEvents = false;
              pageBreak.init(this.view.backgroundPane);
              pageBreak.redraw();

              this.horizontalPageBreaks[i] = pageBreak;
            }
          }

          for (let i = horizontalCount; i < this.horizontalPageBreaks.length; i++) {
            this.horizontalPageBreaks[i].destroy();
          }

          this.horizontalPageBreaks.splice(
            horizontalCount,
            this.horizontalPageBreaks.length - horizontalCount
          );
        }

        if (this.verticalPageBreaks == null && verticalCount > 0) {
          this.verticalPageBreaks = [];
        }

        if (this.verticalPageBreaks != null) {
          for (let i = 0; i <= verticalCount; i++) {
            const pts = [
              new mxPoint(bounds2.x, bounds2.y + (i + 1) * bounds.height),
              new mxPoint(right, bounds2.y + (i + 1) * bounds.height)
            ];

            if (this.verticalPageBreaks[i] != null) {
              this.verticalPageBreaks[i].points = pts;
              this.verticalPageBreaks[i].redraw();
            } else {
              const pageBreak = new mxPolyline(pts, this.pageBreakColor);

              pageBreak.dialect = this.dialect;
              pageBreak.isDashed = this.pageBreakDashed;
              pageBreak.pointerEvents = false;
              pageBreak.init(this.view.backgroundPane);
              pageBreak.redraw();

              this.verticalPageBreaks[i] = pageBreak;
            }
          }

          for (let i = verticalCount; i < this.verticalPageBreaks.length; i++) {
            this.verticalPageBreaks[i].destroy();
          }

          this.verticalPageBreaks.splice(
            verticalCount,
            this.verticalPageBreaks.length - verticalCount
          );
        }
      };

      // Enables snapping to off-grid terminals for edge waypoints
      mxEdgeHandler.prototype.snapToTerminals = true;

      // Disables cloning of flowline by ctrl-drag
      mxEdgeHandler.prototype.cloneEnabled = false;

      // Enables guides
      mxGraphHandler.prototype.guidesEnabled = true;

      // Disables removing relative children from parents
      const mxGraphHandlerShouldRemoveCellsFromParent =
        mxGraphHandler.prototype.shouldRemoveCellsFromParent;

      mxGraphHandler.prototype.shouldRemoveCellsFromParent = function (parent, cells, evt) {
        for (let i = 0; i < cells.length; i++) {
          if (this.graph.getModel().isVertex(cells[i])) {
            const geo = this.graph.getCellGeometry(cells[i]);

            if (geo != null && geo.relative) {
              return false;
            }
          }
        }

        return mxGraphHandlerShouldRemoveCellsFromParent.apply(this, arguments);
      };

      // Alt-move disables guides
      mxGuide.prototype.isEnabledForEvent = function (evt) {
        return !mxEvent.isAltDown(evt);
      };

      // Consumes click events for disabled menu items
      const mxPopupMenuAddItem = mxPopupMenu.prototype.addItem;

      mxPopupMenu.prototype.addItem = function (title, image, funct, parent, iconCls, enabled) {
        const result = mxPopupMenuAddItem.apply(this, arguments);

        if (enabled != null && !enabled) {
          mxEvent.addListener(result, 'mousedown', function (evt) {
            mxEvent.consume(evt);
          });
        }

        return result;
      };

      // Selects descendants before children selection mode
      const graphHandlerGetInitialCellForEvent = mxGraphHandler.prototype.getInitialCellForEvent;

      mxGraphHandler.prototype.getInitialCellForEvent = function (me) {
        const model = this.graph.getModel();
        const psel = model.getParent(this.graph.getSelectionCell());
        let cell = graphHandlerGetInitialCellForEvent.apply(this, arguments);
        let parent = model.getParent(cell);

        if (psel == null || (psel != cell && psel != parent)) {
          while (
            !this.graph.isCellSelected(cell) &&
            !this.graph.isCellSelected(parent) &&
            model.isVertex(parent) &&
            !this.graph.isValidRoot(parent)
          ) {
            cell = parent;
            parent = this.graph.getModel().getParent(cell);
          }
        }

        return cell;
      };

      // Selection is delayed to mouseup if child selected
      const graphHandlerIsDelayedSelection = mxGraphHandler.prototype.isDelayedSelection;

      mxGraphHandler.prototype.isDelayedSelection = function (cell, me) {
        let result = graphHandlerIsDelayedSelection.apply(this, arguments);
        const model = this.graph.getModel();
        const psel = model.getParent(this.graph.getSelectionCell());
        const parent = model.getParent(cell);

        if (
          (psel == null || (psel != cell && psel != parent)) &&
          !this.graph.isCellSelected(cell) &&
          model.isVertex(parent) &&
          !this.graph.isValidRoot(parent)
        ) {
          result = true;
        }

        return result;
      };

      // Delayed selection of parent group
      mxGraphHandler.prototype.selectDelayed = function (me) {
        if (!this.graph.popupMenuHandler.isPopupTrigger(me)) {
          let cell = me.getCell();

          if (cell == null) {
            cell = this.cell;
          }

          // Selects folded cell for hit on folding icon
          const state = this.graph.view.getState(cell);

          if (state != null && me.isSource(state.control)) {
            this.graph.selectCellForEvent(cell, me.getEvent());
          } else {
            const model = this.graph.getModel();
            let parent = model.getParent(cell);

            while (
              this.graph.isCellSelected(cell) &&
              model.isVertex(parent) &&
              !this.graph.isValidRoot(parent)
            ) {
              cell = parent;
              parent = model.getParent(cell);
            }

            this.graph.selectCellForEvent(cell, me.getEvent());
          }
        }
      };

      // Returns last selected ancestor
      mxPopupMenuHandler.prototype.getCellForPopupEvent = function (me) {
        let cell = me.getCell();
        const model = this.graph.getModel();
        let parent = model.getParent(cell);

        while (model.isVertex(parent) && !this.graph.isValidRoot(parent)) {
          if (this.graph.isCellSelected(parent)) {
            cell = parent;
          }

          parent = model.getParent(parent);
        }

        return cell;
      };
    };

    /**
     * Creates and returns a new undo manager.
     */
    Editor.prototype.destroy = function () {
      if (this.graph != null) {
        this.graph.destroy();
        this.graph = null;
      }
    };

    this.q9Editor = Editor;
  }
}

// All the below code is related to flowlines fix.
// It should be placed outside of editor/editor-ui/graph classes
// because it includes some initializations which must run BEFORE editor/graph init

const getJettySize = mxEdgeStyle.getJettySize;

mxEdgeStyle.getJettySize = function (state, isSource) {
  return getJettySize(state, null, null, null, isSource);
};

mxEdgeStyle.scalePointArray = function (points, scale) {
  let result = [];

  if (points != null) {
    for (let i = 0; i < points.length; i++) {
      if (points[i] != null) {
        result[i] = new mxPoint(
          Math.round((points[i].x / scale) * 10) / 10,
          Math.round((points[i].y / scale) * 10) / 10
        );
      } else {
        result[i] = null;
      }
    }
  } else {
    result = null;
  }

  return result;
};

mxEdgeStyle.scaleCellState = function (state, scale) {
  let result = null;

  if (state != null) {
    result = state.clone();
    result.setRect(
      Math.round((state.x / scale) * 10) / 10,
      Math.round((state.y / scale) * 10) / 10,
      Math.round((state.width / scale) * 10) / 10,
      Math.round((state.height / scale) * 10) / 10
    );
  } else {
    result = null;
  }

  return result;
};

mxEdgeStyle.SegmentConnector = function (state, sourceScaled, targetScaled, controlHints, result) {
  // Creates array of all way- and terminalpoints
  const pts = mxEdgeStyle.scalePointArray(state.absolutePoints, state.view.scale);
  const source = mxEdgeStyle.scaleCellState(sourceScaled, state.view.scale);
  const target = mxEdgeStyle.scaleCellState(targetScaled, state.view.scale);
  const tol = 3.5;

  // Whether the first segment outgoing from the source end is horizontal
  let lastPushed = result.length > 0 ? result[0] : null;
  let horizontal = true;
  let hint = null;

  function pushPoint(pt) {
    pt.x = Math.round(pt.x * state.view.scale * 10) / 10;
    pt.y = Math.round(pt.y * state.view.scale * 10) / 10;

    if (
      lastPushed == null ||
      Math.abs(lastPushed.x - pt.x) >= tol * state.view.scale ||
      Math.abs(lastPushed.y - pt.y) >= tol * state.view.scale
    ) {
      result.push(pt);
      lastPushed = pt;
    }

    return lastPushed;
  }

  // Adds the first point
  let pt = pts[0];

  if (pt == null && source != null) {
    pt = new mxPoint(state.view.getRoutingCenterX(source), state.view.getRoutingCenterY(source));
  } else if (pt != null) {
    pt = pt.clone();
  }

  const lastInx = pts.length - 1;

  // Adds the waypoints
  if (controlHints != null && controlHints.length > 0) {
    // Converts all hints and removes nulls
    let hints = [];

    for (let i = 0; i < controlHints.length; i++) {
      const tmp = state.view.transformControlPoint(state, controlHints[i], true);

      if (tmp != null) {
        hints.push(tmp);
      }
    }

    if (hints.length === 0) {
      return;
    }

    // Aligns source and target hint to fixed points
    if (pt != null && hints[0] != null) {
      if (Math.abs(hints[0].x - pt.x) < tol) {
        hints[0].x = pt.x;
      }

      if (Math.abs(hints[0].y - pt.y) < tol) {
        hints[0].y = pt.y;
      }
    }

    const pe = pts[lastInx];

    if (pe != null && hints[hints.length - 1] != null) {
      if (Math.abs(hints[hints.length - 1].x - pe.x) < tol) {
        hints[hints.length - 1].x = pe.x;
      }

      if (Math.abs(hints[hints.length - 1].y - pe.y) < tol) {
        hints[hints.length - 1].y = pe.y;
      }
    }

    hint = hints[0];

    let currentTerm = source;
    let currentPt = pts[0];
    let hozChan = false;
    let vertChan = false;
    let currentHint = hint;

    if (currentPt != null) {
      currentTerm = null;
    }

    // Check for alignment with fixed points and with channels
    // at source and target segments only
    for (let i = 0; i < 2; i++) {
      const fixedVertAlign = currentPt != null && currentPt.x === currentHint.x;
      const fixedHozAlign = currentPt != null && currentPt.y === currentHint.y;

      const inHozChan =
        currentTerm != null &&
        currentHint.y >= currentTerm.y &&
        currentHint.y <= currentTerm.y + currentTerm.height;
      const inVertChan =
        currentTerm != null &&
        currentHint.x >= currentTerm.x &&
        currentHint.x <= currentTerm.x + currentTerm.width;

      hozChan = fixedHozAlign || (currentPt == null && inHozChan);
      vertChan = fixedVertAlign || (currentPt == null && inVertChan);

      // If the current hint falls in both the hor and vert channels in the case
      // of a floating port, or if the hint is exactly co-incident with a
      // fixed point, ignore the source and try to work out the orientation
      // from the target end
      if (!(i === 0 && ((hozChan && vertChan) || (fixedVertAlign && fixedHozAlign)))) {
        if (currentPt != null && !fixedHozAlign && !fixedVertAlign && (inHozChan || inVertChan)) {
          horizontal = !inHozChan;
          break;
        }

        if (vertChan || hozChan) {
          horizontal = hozChan;

          if (i === 1) {
            // Work back from target end
            horizontal = hints.length % 2 === 0 ? hozChan : vertChan;
          }

          break;
        }
      }

      currentTerm = target;
      currentPt = pts[lastInx];

      if (currentPt != null) {
        currentTerm = null;
      }

      currentHint = hints[hints.length - 1];

      if (fixedVertAlign && fixedHozAlign) {
        hints = hints.slice(1);
      }
    }

    if (
      horizontal &&
      ((pts[0] != null && pts[0].y !== hint.y) ||
        (pts[0] == null &&
          source != null &&
          (hint.y < source.y || hint.y > source.y + source.height)))
    ) {
      pushPoint(new mxPoint(pt.x, hint.y));
    } else if (
      !horizontal &&
      ((pts[0] != null && pts[0].x !== hint.x) ||
        (pts[0] == null &&
          source != null &&
          (hint.x < source.x || hint.x > source.x + source.width)))
    ) {
      pushPoint(new mxPoint(hint.x, pt.y));
    }

    if (horizontal) {
      pt.y = hint.y;
    } else {
      pt.x = hint.x;
    }

    for (let i = 0; i < hints.length; i++) {
      horizontal = !horizontal;
      hint = hints[i];

      if (horizontal) {
        pt.y = hint.y;
      } else {
        pt.x = hint.x;
      }

      pushPoint(pt.clone());
    }
  } else {
    hint = pt;
    horizontal = true;
  }

  // Adds the last point
  pt = pts[lastInx];

  if (pt == null && target != null) {
    pt = new mxPoint(state.view.getRoutingCenterX(target), state.view.getRoutingCenterY(target));
  }

  if (pt != null) {
    if (hint != null) {
      if (
        horizontal &&
        ((pts[lastInx] != null && pts[lastInx].y !== hint.y) ||
          (pts[lastInx] == null &&
            target != null &&
            (hint.y < target.y || hint.y > target.y + target.height)))
      ) {
        pushPoint(new mxPoint(pt.x, hint.y));
      } else if (
        !horizontal &&
        ((pts[lastInx] != null && pts[lastInx].x !== hint.x) ||
          (pts[lastInx] == null &&
            target != null &&
            (hint.x < target.x || hint.x > target.x + target.width)))
      ) {
        pushPoint(new mxPoint(hint.x, pt.y));
      }
    }
  }

  // Removes bends inside the source terminal for floating ports
  if (pts[0] == null && source != null) {
    while (
      result.length > 1 &&
      result[1] != null &&
      mxUtils.contains(source, result[1].x, result[1].y)
    ) {
      result.splice(1, 1);
    }
  }

  // Removes bends inside the target terminal
  if (pts[lastInx] == null && target != null) {
    while (
      result.length > 1 &&
      result[result.length - 1] != null &&
      mxUtils.contains(target, result[result.length - 1].x, result[result.length - 1].y)
    ) {
      result.splice(result.length - 1, 1);
    }
  }

  const pe = pts[lastInx];

  // Removes last point if inside tolerance with end point
  if (
    pe != null &&
    result[result.length - 1] != null &&
    Math.abs(pe.x - result[result.length - 1].x) <= tol &&
    Math.abs(pe.y - result[result.length - 1].y) <= tol
  ) {
    result.splice(result.length - 1, 1);

    // Lines up second last point in result with end point
    if (result[result.length - 1] != null) {
      if (Math.abs(result[result.length - 1].x - pe.x) < tol) {
        result[result.length - 1].x = pe.x;
      }

      if (Math.abs(result[result.length - 1].y - pe.y) < tol) {
        result[result.length - 1].y = pe.y;
      }
    }
  }
};

mxEdgeStyle.OrthConnector = function (state, sourceScaled, targetScaled, controlHints, result) {
  const graph = state.view.graph;
  const sourceEdge = sourceScaled == null ? false : graph.getModel().isEdge(sourceScaled.cell);
  const targetEdge = targetScaled == null ? false : graph.getModel().isEdge(targetScaled.cell);

  const pts = mxEdgeStyle.scalePointArray(state.absolutePoints, state.view.scale);
  const source = mxEdgeStyle.scaleCellState(sourceScaled, state.view.scale);
  const target = mxEdgeStyle.scaleCellState(targetScaled, state.view.scale);

  const isTheSameCell = source?.cell?.id === target?.cell?.id && source; // check if connection is loop

  const p0 = pts[0];
  const pe = pts[pts.length - 1];

  const pointStartX: number = p0?.x || 0;
  const pointStartY: number = p0?.y || 0;
  const pointEndX: number = pe?.x || 0;
  const pointEndY: number = pe?.y || 0;

  let sourceX = source != null ? source.x : pointStartX;
  let sourceY = source != null ? source.y : pointStartY;
  let sourceWidth = source != null ? source.width : 0;
  let sourceHeight = source != null ? source.height : 0;

  let targetX = target != null ? target.x : pointEndX;
  let targetY = target != null ? target.y : pointEndY;
  let targetWidth = target != null ? target.width : 0;
  let targetHeight = target != null ? target.height : 0;

  let sourceBuffer = mxEdgeStyle.getJettySize(state, true);
  let targetBuffer = mxEdgeStyle.getJettySize(state, false);

  // Workaround for loop routing within buffer zone
  if (source != null && target === source) {
    targetBuffer = Math.max(sourceBuffer, targetBuffer);
    sourceBuffer = targetBuffer;
  }

  const totalBuffer = targetBuffer + sourceBuffer;

  let tooShort = false;

  // Checks minimum distance for fixed points and falls back to segment connector
  if (p0 != null && pe != null && !isTheSameCell) {
    const dx = pointEndX - pointStartX;
    const dy = pointEndY - pointStartY;

    tooShort = dx * dx + dy * dy < totalBuffer * totalBuffer;
  }

  if (
    tooShort ||
    (mxEdgeStyle.orthPointsFallback && controlHints != null && controlHints.length > 0) ||
    sourceEdge ||
    targetEdge
  ) {
    mxEdgeStyle.SegmentConnector(state, sourceScaled, targetScaled, controlHints, result);

    return;
  }

  // Determine the side(s) of the source and target vertices
  // that the edge may connect to
  // portConstraint [source, target]
  const portConstraint = [mxConstants.DIRECTION_MASK_ALL, mxConstants.DIRECTION_MASK_ALL];
  let rotation = 0;

  if (source != null) {
    portConstraint[0] = mxUtils.getPortConstraints(
      source,
      state,
      true,
      mxConstants.DIRECTION_MASK_ALL
    );
    rotation = mxUtils.getValue(source.style, mxConstants.STYLE_ROTATION, 0);

    if (rotation !== 0) {
      const newRect = mxUtils.getBoundingBox(
        new mxRectangle(sourceX, sourceY, sourceWidth, sourceHeight),
        rotation
      );

      sourceX = newRect.x;
      sourceY = newRect.y;
      sourceWidth = newRect.width;
      sourceHeight = newRect.height;
    }
  }

  if (target != null) {
    portConstraint[1] = mxUtils.getPortConstraints(
      target,
      state,
      false,
      mxConstants.DIRECTION_MASK_ALL
    );
    rotation = mxUtils.getValue(target.style, mxConstants.STYLE_ROTATION, 0);

    if (rotation !== 0) {
      const newRect = mxUtils.getBoundingBox(
        new mxRectangle(targetX, targetY, targetWidth, targetHeight),
        rotation
      );

      targetX = newRect.x;
      targetY = newRect.y;
      targetWidth = newRect.width;
      targetHeight = newRect.height;
    }
  }

  const dir = [0, 0];

  // Work out which faces of the vertices present against each other
  // in a way that would allow a 3-segment connection if port constraints
  // permitted.
  // geo -> [source, target] [x, y, width, height]
  const geo = [
    [sourceX, sourceY, sourceWidth, sourceHeight],
    [targetX, targetY, targetWidth, targetHeight]
  ];
  const buffer = [sourceBuffer, targetBuffer];

  for (let i = 0; i < 2; i++) {
    mxEdgeStyle.limits[i][1] = geo[i][0] - buffer[i];
    mxEdgeStyle.limits[i][2] = geo[i][1] - buffer[i];
    mxEdgeStyle.limits[i][4] = geo[i][0] + geo[i][2] + buffer[i];
    mxEdgeStyle.limits[i][8] = geo[i][1] + geo[i][3] + buffer[i];
  }

  // Work out which quad the target is in
  const sourceCenX = geo[0][0] + geo[0][2] / 2.0;
  const sourceCenY = geo[0][1] + geo[0][3] / 2.0;
  const targetCenX = geo[1][0] + geo[1][2] / 2.0;
  const targetCenY = geo[1][1] + geo[1][3] / 2.0;

  let dx, dy;

  if (!isTheSameCell) {
    dx = sourceCenX - targetCenX;
    dy = sourceCenY - targetCenY;
  } else {
    dx = pointStartX - pointEndX;
    dy = pointStartY - pointEndY;
  }

  let quad = 0;

  // 0 | 1
  // -----
  // 3 | 2

  if (isTheSameCell) {
    const MAX_DEVIATION = 1;

    const isClose = (a, b) => Math.abs(Math.round(a) - Math.round(b)) <= MAX_DEVIATION;

    const onSameX = dx === 0;
    const onSameY = dy === 0;
    const targetBelow = dy < 0;
    const targetRight = dx < 0;

    const mainlyOnRight = 2 * sourceCenX - (pointStartX + pointEndX) < 0;
    const mainlyOnTop = 2 * sourceCenY - (pointStartY + pointEndY) > 0;

    const sourceBorderPosition = {
      right: isClose(sourceX + sourceWidth, pointStartX),
      left: isClose(sourceX, pointStartX),
      top: isClose(sourceY, pointStartY),
      bottom: isClose(sourceY + sourceHeight, pointStartY)
    };

    const targetBorderPosition = {
      right: isClose(targetX + targetWidth, pointEndX),
      left: isClose(targetX, pointEndX),
      top: isClose(targetY, pointEndY),
      bottom: isClose(targetY + targetHeight, pointEndY)
    };

    if (onSameX) {
      if (sourceBorderPosition.right) {
        quad = targetBelow ? 2 : 1;
      } else {
        quad = targetBelow ? 3 : 0;
      }
    } else if (onSameY) {
      if (sourceBorderPosition.top) {
        quad = targetRight ? 1 : 0;
      } else {
        quad = targetRight ? 2 : 3;
      }
    } else if (targetBelow) {
      if (sourceBorderPosition.right) {
        if (targetBorderPosition.bottom) {
          quad = 2;
        } else if (targetBorderPosition.left) {
          mainlyOnTop ? (quad = 0) : (quad = 3);
        }
      } else if (sourceBorderPosition.left) {
        if (targetBorderPosition.bottom) {
          quad = 3;
        } else if (targetBorderPosition.right) {
          mainlyOnTop ? (quad = 1) : (quad = 2);
        }
      } else if (sourceBorderPosition.top) {
        if (targetBorderPosition.right) {
          quad = 1;
        } else if (targetBorderPosition.left) {
          quad = 0;
        } else if (mainlyOnRight) {
          quad = 2;
        } else {
          quad = 3;
        }
      }
    } else {
      if (sourceBorderPosition.right) {
        if (targetBorderPosition.top) {
          quad = 1;
        } else if (targetBorderPosition.left) {
          mainlyOnTop ? (quad = 0) : (quad = 3);
        }
      } else if (sourceBorderPosition.left) {
        if (targetBorderPosition.top) {
          quad = 0;
        } else if (targetBorderPosition.right) {
          mainlyOnTop ? (quad = 1) : (quad = 2);
        }
      } else if (sourceBorderPosition.bottom) {
        if (targetBorderPosition.right) {
          quad = 2;
        } else if (targetBorderPosition.left) {
          quad = 3;
        } else if (mainlyOnRight) {
          quad = 1;
        } else {
          quad = 0;
        }
      }
    }
  } else {
    if (dx < 0) {
      if (dy < 0) {
        quad = 2;
      } else {
        quad = 1;
      }
    } else {
      if (dy <= 0) {
        quad = 3;

        // Special case on x = 0 and negative y
        if (dx === 0) {
          quad = 2;
        }
      }
    }
  }

  // Check for connection constraints
  let currentTerm = null;

  if (source != null) {
    currentTerm = p0;
  }

  const constraint = [
    [0.5, 0.5],
    [0.5, 0.5]
  ];

  for (let i = 0; i < 2; i++) {
    if (currentTerm != null) {
      constraint[i][0] = (currentTerm.x - geo[i][0]) / geo[i][2];

      if (Math.abs(currentTerm.x - geo[i][0]) <= 1) {
        dir[i] = mxConstants.DIRECTION_MASK_WEST;
      } else if (Math.abs(currentTerm.x - geo[i][0] - geo[i][2]) <= 1) {
        dir[i] = mxConstants.DIRECTION_MASK_EAST;
      }

      constraint[i][1] = (currentTerm.y - geo[i][1]) / geo[i][3];

      if (Math.abs(currentTerm.y - geo[i][1]) <= 1) {
        dir[i] = mxConstants.DIRECTION_MASK_NORTH;
      } else if (Math.abs(currentTerm.y - geo[i][1] - geo[i][3]) <= 1) {
        dir[i] = mxConstants.DIRECTION_MASK_SOUTH;
      }
    }

    currentTerm = null;

    if (target != null) {
      currentTerm = pe;
    }
  }

  const sourceTopDist = geo[0][1] - (geo[1][1] + geo[1][3]);
  const sourceLeftDist = geo[0][0] - (geo[1][0] + geo[1][2]);
  const sourceBottomDist = geo[1][1] - (geo[0][1] + geo[0][3]);
  const sourceRightDist = geo[1][0] - (geo[0][0] + geo[0][2]);

  mxEdgeStyle.vertexSeperations[1] = Math.max(sourceLeftDist - totalBuffer, 0);
  mxEdgeStyle.vertexSeperations[2] = Math.max(sourceTopDist - totalBuffer, 0);
  mxEdgeStyle.vertexSeperations[4] = Math.max(sourceBottomDist - totalBuffer, 0);
  mxEdgeStyle.vertexSeperations[3] = Math.max(sourceRightDist - totalBuffer, 0);

  // Start of source and target direction determination

  // Work through the preferred orientations by relative positioning
  // of the vertices and list them in preferred and available order

  const dirPref = [];
  const horPref = [];
  const vertPref = [];

  horPref[0] =
    sourceLeftDist >= sourceRightDist
      ? mxConstants.DIRECTION_MASK_WEST
      : mxConstants.DIRECTION_MASK_EAST;
  vertPref[0] =
    sourceTopDist >= sourceBottomDist
      ? mxConstants.DIRECTION_MASK_NORTH
      : mxConstants.DIRECTION_MASK_SOUTH;

  horPref[1] = mxUtils.reversePortConstraints(horPref[0]);
  vertPref[1] = mxUtils.reversePortConstraints(vertPref[0]);

  const preferredHorizDist = sourceLeftDist >= sourceRightDist ? sourceLeftDist : sourceRightDist;
  const preferredVertDist = sourceTopDist >= sourceBottomDist ? sourceTopDist : sourceBottomDist;

  const prefOrdering = [
    [0, 0],
    [0, 0]
  ];
  let preferredOrderSet = false;

  // If the preferred port isn't available, switch it
  for (let i = 0; i < 2; i++) {
    if (dir[i] !== 0x0) {
      continue;
    }

    if ((horPref[i] & portConstraint[i]) === 0) {
      horPref[i] = mxUtils.reversePortConstraints(horPref[i]);
    }

    if ((vertPref[i] & portConstraint[i]) === 0) {
      vertPref[i] = mxUtils.reversePortConstraints(vertPref[i]);
    }

    prefOrdering[i][0] = vertPref[i];
    prefOrdering[i][1] = horPref[i];
  }

  if (preferredVertDist > 0 && preferredHorizDist > 0) {
    // Possibility of two segment edge connection
    if ((horPref[0] & portConstraint[0]) > 0 && (vertPref[1] & portConstraint[1]) > 0) {
      prefOrdering[0][0] = horPref[0];
      prefOrdering[0][1] = vertPref[0];
      prefOrdering[1][0] = vertPref[1];
      prefOrdering[1][1] = horPref[1];
      preferredOrderSet = true;
    } else if ((vertPref[0] & portConstraint[0]) > 0 && (horPref[1] & portConstraint[1]) > 0) {
      prefOrdering[0][0] = vertPref[0];
      prefOrdering[0][1] = horPref[0];
      prefOrdering[1][0] = horPref[1];
      prefOrdering[1][1] = vertPref[1];
      preferredOrderSet = true;
    }
  }

  if (preferredVertDist > 0 && !preferredOrderSet) {
    prefOrdering[0][0] = vertPref[0];
    prefOrdering[0][1] = horPref[0];
    prefOrdering[1][0] = vertPref[1];
    prefOrdering[1][1] = horPref[1];
    preferredOrderSet = true;
  }

  if (preferredHorizDist > 0 && !preferredOrderSet) {
    prefOrdering[0][0] = horPref[0];
    prefOrdering[0][1] = vertPref[0];
    prefOrdering[1][0] = horPref[1];
    prefOrdering[1][1] = vertPref[1];
    preferredOrderSet = true;
  }

  // The source and target prefs are now an ordered list of
  // the preferred port selections
  // If the list contains gaps, compact it

  for (let i = 0; i < 2; i++) {
    if (dir[i] != 0x0) {
      continue;
    }

    if ((prefOrdering[i][0] & portConstraint[i]) === 0) {
      prefOrdering[i][0] = prefOrdering[i][1];
    }

    dirPref[i] = prefOrdering[i][0] & portConstraint[i];
    dirPref[i] |= (prefOrdering[i][1] & portConstraint[i]) << 8;
    dirPref[i] |= (prefOrdering[1 - i][i] & portConstraint[i]) << 16;
    dirPref[i] |= (prefOrdering[1 - i][1 - i] & portConstraint[i]) << 24;

    if ((dirPref[i] & 0xf) == 0) {
      dirPref[i] = dirPref[i] << 8;
    }

    if ((dirPref[i] & 0xf00) == 0) {
      dirPref[i] = (dirPref[i] & 0xf) | (dirPref[i] >> 8);
    }

    if ((dirPref[i] & 0xf0000) == 0) {
      dirPref[i] = (dirPref[i] & 0xffff) | ((dirPref[i] & 0xf000000) >> 8);
    }

    dir[i] = dirPref[i] & 0xf;

    if (
      portConstraint[i] === mxConstants.DIRECTION_MASK_WEST ||
      portConstraint[i] === mxConstants.DIRECTION_MASK_NORTH ||
      portConstraint[i] === mxConstants.DIRECTION_MASK_EAST ||
      portConstraint[i] === mxConstants.DIRECTION_MASK_SOUTH
    ) {
      dir[i] = portConstraint[i];
    }
  }

  // End of source and target direction determination

  let sourceIndex = dir[0] === mxConstants.DIRECTION_MASK_EAST ? 3 : dir[0];
  let targetIndex = dir[1] === mxConstants.DIRECTION_MASK_EAST ? 3 : dir[1];

  sourceIndex -= quad;
  targetIndex -= quad;

  if (sourceIndex < 1) {
    sourceIndex += 4;
  }

  if (targetIndex < 1) {
    targetIndex += 4;
  }

  const routePattern = mxEdgeStyle.routePatterns[sourceIndex - 1][targetIndex - 1];

  mxEdgeStyle.wayPoints1[0][0] = geo[0][0];
  mxEdgeStyle.wayPoints1[0][1] = geo[0][1];

  switch (dir[0]) {
    case mxConstants.DIRECTION_MASK_WEST:
      mxEdgeStyle.wayPoints1[0][0] -= sourceBuffer;
      mxEdgeStyle.wayPoints1[0][1] += constraint[0][1] * geo[0][3];
      break;

    case mxConstants.DIRECTION_MASK_SOUTH:
      mxEdgeStyle.wayPoints1[0][0] += constraint[0][0] * geo[0][2];
      mxEdgeStyle.wayPoints1[0][1] += geo[0][3] + sourceBuffer;
      break;

    case mxConstants.DIRECTION_MASK_EAST:
      mxEdgeStyle.wayPoints1[0][0] += geo[0][2] + sourceBuffer;
      mxEdgeStyle.wayPoints1[0][1] += constraint[0][1] * geo[0][3];
      break;

    case mxConstants.DIRECTION_MASK_NORTH:
      mxEdgeStyle.wayPoints1[0][0] += constraint[0][0] * geo[0][2];
      mxEdgeStyle.wayPoints1[0][1] -= sourceBuffer;
      break;
  }

  let currentIndex = 0;

  // Orientation, 0 horizontal, 1 vertical
  let lastOrientation =
    (dir[0] & (mxConstants.DIRECTION_MASK_EAST | mxConstants.DIRECTION_MASK_WEST)) > 0 ? 0 : 1;
  const initialOrientation = lastOrientation;
  let currentOrientation = 0;

  for (let i = 0; i < routePattern.length; i++) {
    const nextDirection = routePattern[i] & 0xf;

    // Rotate the index of this direction by the quad
    // to get the real direction
    let directionIndex = nextDirection === mxConstants.DIRECTION_MASK_EAST ? 3 : nextDirection;

    directionIndex += quad;

    if (directionIndex > 4) {
      directionIndex -= 4;
    }

    const direction = mxEdgeStyle.dirVectors[directionIndex - 1];

    currentOrientation = directionIndex % 2 > 0 ? 0 : 1;

    // Only update the current index if the point moved
    // in the direction of the current segment move,
    // otherwise the same point is moved until there is
    // a segment direction change
    if (currentOrientation !== lastOrientation) {
      currentIndex++;
      // Copy the previous way point into the new one
      // We can't base the new position on index - 1
      // because sometime elbows turn out not to exist,
      // then we'd have to rewind.
      mxEdgeStyle.wayPoints1[currentIndex][0] = mxEdgeStyle.wayPoints1[currentIndex - 1][0];
      mxEdgeStyle.wayPoints1[currentIndex][1] = mxEdgeStyle.wayPoints1[currentIndex - 1][1];
    }

    const tar = (routePattern[i] & mxEdgeStyle.TARGET_MASK) > 0;
    const sou = (routePattern[i] & mxEdgeStyle.SOURCE_MASK) > 0;
    let side = (routePattern[i] & mxEdgeStyle.SIDE_MASK) >> 5;

    side = side << quad;

    if (side > 0xf) {
      side = side >> 4;
    }

    const center = (routePattern[i] & mxEdgeStyle.CENTER_MASK) > 0;

    if ((sou || tar) && side < 9) {
      let limit = 0;
      const souTar = sou ? 0 : 1;

      if (center && currentOrientation === 0) {
        limit = geo[souTar][0] + constraint[souTar][0] * geo[souTar][2];
      } else if (center) {
        limit = geo[souTar][1] + constraint[souTar][1] * geo[souTar][3];
      } else {
        limit = mxEdgeStyle.limits[souTar][side];
      }

      if (currentOrientation === 0) {
        const lastX = mxEdgeStyle.wayPoints1[currentIndex][0];
        const deltaX = (limit - lastX) * direction[0];

        if (deltaX > 0) {
          mxEdgeStyle.wayPoints1[currentIndex][0] += direction[0] * deltaX;
        }
      } else {
        const lastY = mxEdgeStyle.wayPoints1[currentIndex][1];
        const deltaY = (limit - lastY) * direction[1];

        if (deltaY > 0) {
          mxEdgeStyle.wayPoints1[currentIndex][1] += direction[1] * deltaY;
        }
      }
    } else if (center) {
      // Which center we're travelling to depend on the current direction
      mxEdgeStyle.wayPoints1[currentIndex][0] +=
        direction[0] * Math.abs(mxEdgeStyle.vertexSeperations[directionIndex] / 2);
      mxEdgeStyle.wayPoints1[currentIndex][1] +=
        direction[1] * Math.abs(mxEdgeStyle.vertexSeperations[directionIndex] / 2);
    }

    if (
      currentIndex > 0 &&
      mxEdgeStyle.wayPoints1[currentIndex][currentOrientation] ==
        mxEdgeStyle.wayPoints1[currentIndex - 1][currentOrientation]
    ) {
      currentIndex--;
    } else {
      lastOrientation = currentOrientation;
    }
  }

  for (let i = 0; i <= currentIndex; i++) {
    if (i === currentIndex) {
      // Last point can cause last segment to be in
      // same direction as jetty/approach. If so,
      // check the number of points is consistent
      // with the relative orientation of source and target
      // jx. Same orientation requires an even
      // number of turns (points), different requires
      // odd.
      const targetOrientation =
        (dir[1] & (mxConstants.DIRECTION_MASK_EAST | mxConstants.DIRECTION_MASK_WEST)) > 0 ? 0 : 1;
      const sameOrient = targetOrientation === initialOrientation ? 0 : 1;

      // (currentIndex + 1) % 2 is 0 for even number of points,
      // 1 for odd
      if (sameOrient !== (currentIndex + 1) % 2) {
        // The last point isn't required
        break;
      }
    }

    result.push(
      new mxPoint(
        Math.round(mxEdgeStyle.wayPoints1[i][0] * state.view.scale * 10) / 10,
        Math.round(mxEdgeStyle.wayPoints1[i][1] * state.view.scale * 10) / 10
      )
    );
  }

  // Removes duplicates
  let index = 1;

  while (index < result.length) {
    if (
      result[index - 1] == null ||
      result[index] == null ||
      result[index - 1].x !== result[index].x ||
      result[index - 1].y !== result[index].y
    ) {
      index++;
    } else {
      result.splice(index, 1);
    }
  }
};

mxStyleRegistry.putValue(mxConstants.EDGESTYLE_ORTHOGONAL, mxEdgeStyle.OrthConnector);
mxStyleRegistry.putValue(mxConstants.EDGESTYLE_SEGMENT, mxEdgeStyle.SegmentConnector);
